import { addDays, addHours, endOfDay, startOfDay } from "date-fns";
import type { DatePreset } from "@/components/data-table/types";
import { matchesPathname } from "@/lib/pathname";

// ----------------------------- VERSION -----------------------
const VERSION = 0.1;
const _PREAMBLE_STORAGE_KEY = `persist-v-${VERSION}`;

// ----------------------------- SECURITY -----------------------
const _SALT_WORK_FACTOR = 10;

const _TOKEN_TYPES = {
  EMAIL_VERIFICATION: "EMAIL_VERIFICATION",
};

const _VERIFICATION_MODES = {
  SIGNUP: "SIGNUP",
  LOGIN: "LOGIN",
};

// ----------------------------- Names -----------------------
const _STORAGE_KEYS_PREFIX = `persist-v${VERSION}_`;
const _RESUME_PDF_FILENAME_PREFIX = `vacwork-v${VERSION}_`;

// ----------------------------- Page sizes -----------------------
const PX_PER_PT = 4 / 3;

// Reference: https://www.prepressure.com/library/paper-size/letter
const LETTER_WIDTH_PT = 612;
const LETTER_HEIGHT_PT = 792;
const _LETTER_WIDTH_PX = LETTER_WIDTH_PT * PX_PER_PT;
const _LETTER_HEIGHT_PX = LETTER_HEIGHT_PT * PX_PER_PT;

// Reference: https://www.prepressure.com/library/paper-size/din-a4
const A4_WIDTH_PT = 595;
const A4_HEIGHT_PT = 842;
export const A4_WIDTH_PX = A4_WIDTH_PT * PX_PER_PT;
export const A4_HEIGHT_PX = A4_HEIGHT_PT * PX_PER_PT;

// ----------------------------- Regex -----------------------
const _PHONE_NUMBER_REGEX =
  /^(\+?\d{0,4})?\s?-?\s?(\(?\d{3}\)?)\s?-?\s?(\(?\d{3}\)?)\s?-?\s?(\(?\d{4}\)?)?$/;

// ----------------------------- MARKETPLACE -----------------------
export const NEARBY_RADIUS = "1000000"; // 1000km in metres

// ----------------------------- PAGES -----------------------------

const _AUTH_PAGES = ["/login"];

export const EMPLOYEE_ONLY_PAGES = ["/applications", "/resumes"];

export const EMPLOYER_ONLY_PAGES = ["/jobs", "/overview", "/organizations"];

const _PRE_AUTH_PAGES = ["/verify"];

const _ONBOARDING_PAGES = ["/onboarding"];

export const ADMIN_ONLY_PAGES = ["/admin"];

const DUAL_PAGES = ["/job-listings"] as const;

export function isDualPage(pathname: string): boolean {
  return DUAL_PAGES.some((page) => matchesPathname(pathname, page));
}

// ----------------------------- NETWORK -----------------------------

export const MAXIMUM_FILE_SIZE = 5 * 1024 * 1024;
export const DEBOUNCE_RATE = 300;
const _TIMEOUT = 5000;
const _MAX_RETRIES = 3;
export const RATE_LIMIT = 60;
const _RATE_LIMIT_PERIOD = "1m";

// ----------------------------- OPTIONS -----------------------------
// TODO: Standardize names

const _PROVINCES = [
  {
    label: "Mpumalanga",
    value: "mpumalanga",
  },

  {
    label: "Gauteng",
    value: "gauteng",
  },

  {
    label: "Western Cape",
    value: "western cape",
  },

  {
    label: "Eastern Cape",
    value: "eastern cape",
  },

  {
    label: "Northern Cape",
    value: "northern cape",
  },

  {
    label: "North west",
    value: "north west",
  },

  {
    label: "Limpopo",
    value: "limpopo",
  },

  {
    label: "KwaZulu-Natal",
    value: "kwazulu-natal",
  },
];

export const EXPERTISE = [
  {
    label: "Novice",
    value: "novice",
  },
  {
    label: "Intermediate",
    value: "intermediate",
  },
  {
    label: "Advanced",
    value: "advanced",
  },
  {
    label: "Expert",
    value: "expert",
  },
];

export const PROFICIENCY = [
  {
    label: "Elementary",
    value: "elementary",
  },
  {
    label: "Intermediate",
    value: "intermediate",
  },
  {
    label: "Fluent",
    value: "fluent",
  },
  {
    label: "Native",
    value: "native",
  },
];

const _role = [
  {
    label: "Recruiter",
    value: "recruiter",
  },
  {
    label: "Owner",
    value: "owner",
  },
  {
    label: "Other",
    value: "other",
  },
];

const _referral = [
  {
    label: "Social media",
    value: "social_media",
  },
  {
    label: "Radio",
    value: "radio",
  },
  {
    label: "Television",
    value: "television",
  },
  {
    label: "Word of mouth",
    value: "word_of_mouth",
  },
  {
    label: "Other",
    value: "other",
  },
];

const _types = [
  {
    label: "Full time",
    value: "full_time",
    description: "Full-time employment.",
  },
  {
    label: "Part time",
    value: "part_time",
    description: "Part-time employment.",
  },
];

export const COMMITMENTS = [
  {
    value: "part_time",
    label: "Part time",
  },
  {
    value: "full_time",
    label: "Full time",
  },
];

export const RESUME_REQUIRED = [
  {
    label: "Required",
    value: "required",
  },
  {
    label: "Not required",
    value: "not_required",
  },
];

export const WORK_MODELS = [
  { label: "Onsite", value: "onsite" },
  { label: "Hybrid", value: "hybrid" },
  { label: "Remote", value: "remote" },
];

export const PRIORITIES = [
  {
    label: "High",
    value: "high",
  },
  {
    label: "Medium",
    value: "medium",
  },
  {
    label: "Low",
    value: "low",
  },
];

export const RATES = [
  {
    value: "hourly",
    label: "Hourly",
  },
  {
    value: "daily",
    label: "Daily",
  },
  {
    value: "weekly",
    label: "Weekly",
  },
  {
    value: "monthly",
    label: "Monthly",
  },
  {
    value: "annually",
    label: "Annually",
  },
];

export const JOB_STATUSES = [
  {
    value: "archived",
    label: "Archived",
  },
  {
    value: "published",
    label: "Published",
  },
  {
    value: "draft",
    label: "Draft",
  },
];

export const STATUS_BADGE_VARIANTS = {
  draft: "amber",
  screening: "sky",
  shortlisted: "amber",
  interviewing: "blue",
  verification_pending: "violet",
  accepted: "green",
  rejected: "red",
  archived: "neutral",
} as const;

export const APPLICATION_STATUSES = [
  {
    value: "draft",
    label: "Draft",
  },
  {
    value: "screening",
    label: "Screening",
  },
  {
    value: "shortlisted",
    label: "Shortlisted",
  },
  {
    value: "interviewing",
    label: "Interviewing",
  },
  {
    value: "verification_pending",
    label: "Verification pending",
  },
  {
    value: "accepted",
    label: "Accepted",
  },
  {
    value: "rejected",
    label: "Rejected",
  },
  {
    value: "archived",
    label: "Archived",
  },
];

export const USER_TYPES = [
  {
    value: "employee",
    label: "Looking for work",
    icon: "briefcase",
    description: "Browse and apply to jobs",
  },
  {
    value: "employer",
    label: "Hiring staff",
    icon: "storefront",
    description: "Post jobs and review applicants",
  },
];

const _TEMPLATES = [
  {
    label: "Universal",
    value: "universal",
  },
  {
    label: "Material",
    value: "material",
  },
];

export const FONTS = [
  {
    value: "inter",
    label: "Inter",
  },
  {
    value: "config-rounded",
    label: "Config Rounded",
  },
  {
    value: "product-sans",
    label: "Product Sans",
  },
  {
    value: "open-sans",
    label: "Open Sans",
  },
  {
    value: "roboto",
    label: "Roboto",
  },
  {
    value: "ibm-plex-sans",
    label: "IBM Plex Sans",
  },
  {
    value: "ubuntu",
    label: "Ubuntu",
  },
  {
    value: "rubik",
    label: "Rubik",
  },
  {
    value: "eb-garamond",
    label: "EB Garamond",
  },
  {
    value: "crimson-text",
    label: "Crimson Text",
  },
  {
    value: "inconsolata",
    label: "Inconsolata",
  },
];

export const PAGE_SIZES = [
  {
    label: "A4",
    value: "a4",
  },
  {
    label: "Letter",
    value: "letter",
  },
];

export const JOB_CATEGORIES = [
  { value: "education_and_teaching", label: "Education and Teaching" },
  { value: "printing_and_press", label: "Printing and Press" },
  { value: "creative_arts_and_design", label: "Creative Arts and Design" },
  { value: "healthcare_and_therapy", label: "Healthcare and Therapy" },
  { value: "technology_and_development", label: "Technology and Development" },
  {
    value: "law_enforcement_and_emergency_services",
    label: "Law Enforcement and Emergency Services",
  },
  { value: "marine_and_fishing", label: "Marine and Fishing" },
  {
    value: "administration_and_coordination",
    label: "Administration and Coordination",
  },
  {
    value: "mechanical_and_technical_work",
    label: "Mechanical and Technical Work",
  },
  { value: "finance_and_accounting", label: "Finance and Accounting" },
  { value: "sales_and_marketing", label: "Sales and Marketing" },
  {
    value: "manufacturing_and_production",
    label: "Manufacturing and Production",
  },
  { value: "legal_and_compliance", label: "Legal and Compliance" },
  {
    value: "transportation_and_logistics",
    label: "Transportation and Logistics",
  },
  {
    value: "agriculture_and_environment",
    label: "Agriculture and Environment",
  },
  { value: "human_resources", label: "Human Resources" },
  {
    value: "engineering_and_construction",
    label: "Engineering and Construction",
  },
  {
    value: "hospitality_and_service_industry",
    label: "Hospitality and Service Industry",
  },
  { value: "science_and_research", label: "Science and Research" },
  {
    value: "miscellaneous_and_specialized_roles",
    label: "Miscellaneous and Specialized Roles",
  },
  {
    value: "customer_service_and_support",
    label: "Customer Service and Support",
  },
  { value: "retail_and_sales", label: "Retail and Sales" },
  { value: "energy_and_utilities", label: "Energy and Utilities" },
  { value: "food_and_beverage", label: "Food and Beverage" },
  { value: "aviation_and_aerospace", label: "Aviation and Aerospace" },
  { value: "legal_and_judicial", label: "Legal and Judicial" },
  {
    value: "government_and_public_sector",
    label: "Government and Public Sector",
  },
  {
    value: "automotive_and_transportation",
    label: "Automotive and Transportation",
  },
  {
    value: "insurance_and_risk_management",
    label: "Insurance and Risk Management",
  },
  { value: "construction_and_trades", label: "Construction and Trades" },
  { value: "science_and_laboratory", label: "Science and Laboratory" },
  { value: "real_estate", label: "Real Estate" },
  {
    value: "social_services_and_non_profit",
    label: "Social Services and Non-Profit",
  },
  {
    value: "pharmaceutical_and_biotechnology",
    label: "Pharmaceutical and Biotechnology",
  },
  { value: "security_and_defense", label: "Security and Defense" },
  { value: "entertainment_and_media", label: "Entertainment and Media" },
  { value: "sports_and_recreation", label: "Sports and Recreation" },
  {
    value: "agriculture_and_horticulture",
    label: "Agriculture and Horticulture",
  },
  {
    value: "manufacturing_and_industrial",
    label: "Manufacturing and Industrial",
  },
  {
    value: "event_planning_and_hospitality",
    label: "Event Planning and Hospitality",
  },
  { value: "adult_entertainment", label: "Adult Entertainment" },
  { value: "arts_and_culture", label: "Arts and Culture" },
  { value: "education_administration", label: "Education Administration" },
  { value: "environmental_services", label: "Environmental Services" },
  { value: "property_management", label: "Property Management" },
  { value: "telecommunications", label: "Telecommunications" },
  { value: "fashion_and_apparel", label: "Fashion and Apparel" },
  { value: "humanitarian_work", label: "Humanitarian Work" },
  {
    value: "pet_care_and_veterinary_services",
    label: "Pet Care and Veterinary Services",
  },
  { value: "public_relations", label: "Public Relations" },
  { value: "event_management", label: "Event Management" },
  { value: "nonprofit_and_charity_work", label: "Nonprofit and Charity Work" },
  {
    value: "television_and_broadcasting",
    label: "Television and Broadcasting",
  },
  { value: "tourism_and_travel", label: "Tourism and Travel" },
  { value: "waste_management", label: "Waste Management" },
  { value: "mental_health_services", label: "Mental Health Services" },
  { value: "home_and_personal_care", label: "Home and Personal Care" },
  { value: "import_and_export", label: "Import and Export" },
  { value: "music_and_performing_arts", label: "Music and Performing Arts" },
  { value: "religious_services", label: "Religious Services" },
  {
    value: "funeral_and_mortuary_services",
    label: "Funeral and Mortuary Services",
  },
  { value: "gaming_and_esports", label: "Gaming and Esports" },
  { value: "consulting", label: "Consulting" },
  {
    value: "personal_coaching_and_development",
    label: "Personal Coaching and Development",
  },
  { value: "remote_and_freelance_work", label: "Remote and Freelance Work" },
  { value: "ethics_and_compliance", label: "Ethics and Compliance" },
  { value: "uncategorized", label: "Uncategorized" },
];

export const presets = [
  {
    label: "Today",
    from: startOfDay(new Date()),
    to: endOfDay(new Date()),
    shortcut: "d", // day
  },
  {
    label: "Yesterday",
    from: startOfDay(addDays(new Date(), -1)),
    to: endOfDay(addDays(new Date(), -1)),
    shortcut: "y",
  },
  {
    label: "Last hour",
    from: addHours(new Date(), -1),
    to: new Date(),
    shortcut: "h",
  },
  {
    label: "Last 7 days",
    from: startOfDay(addDays(new Date(), -7)),
    to: endOfDay(new Date()),
    shortcut: "w",
  },
  {
    label: "Last 14 days",
    from: startOfDay(addDays(new Date(), -14)),
    to: endOfDay(new Date()),
    shortcut: "b", // bi-weekly
  },
  {
    label: "Last 30 days",
    from: startOfDay(addDays(new Date(), -30)),
    to: endOfDay(new Date()),
    shortcut: "m",
  },
] satisfies DatePreset[];
